import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { FormsService } from '@portal-core/forms/services/forms.service';
import { CancelableEvent } from '@portal-core/general/classes/cancelable-event';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { UsersService } from '@portal-core/users/services/users.service';
import { LoadingState } from '@portal-core/util/loading-state';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { FormControlErrorMessageComponent } from '@portal-core/forms/components/form-control-error-message/form-control-error-message.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { ErrorsExpansionPanelComponent } from '@portal-core/general/components/errors-expansion-panel/errors-expansion-panel.component';
import { MatButton } from '@angular/material/button';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';

@Component({
    selector: 'mc-user-email-form',
    templateUrl: './user-email-form.component.html',
    styleUrls: ['./user-email-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, MatFormField, McColorDirective, MatLabel, MatInput, MatError, FormControlErrorMessageComponent, NgIf, ErrorsExpansionPanelComponent, MatButton, LoaderComponent, AsyncPipe]
})
export class UserEmailFormComponent implements OnInit {
  private readonly snackBar = inject(MatSnackBar);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly formsService = inject(FormsService);
  private readonly usersService = inject(UsersService);
  private readonly errorService = inject(ErrorService);
  @Input() licenseUser: LicenseUser;
  @Output() canceled: EventEmitter<void> = new EventEmitter<void>();
  @Output() save: EventEmitter<CancelableEvent> = new EventEmitter<CancelableEvent>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('formDirective', { static: true }) formDirective: FormGroupDirective;

  get dirty(): boolean {
    return this.emailForm?.dirty;
  }

  savingState: LoadingState<string> = new LoadingState<string>();
  showCancelButton: boolean;
  emailForm: UntypedFormGroup;
  ngOnInit() {
    this.showCancelButton = this.canceled.observers.length > 0;
    this.buildForm();
  }

  onCancelClicked() {
    this.canceled.emit();
  }

  onSubmit(formGroup: UntypedFormGroup) {
    if (!formGroup.valid) {
      return;
    }
    const saveEvent = new CancelableEvent();
    this.save.emit(saveEvent);

    if (!saveEvent.defaultPrevented) {
      this.savingState.update(true);

      this.usersService.updateEmail$(this.licenseUser, formGroup.value['newEmail']).subscribe(() => {
        this.savingState.update(false);
        this.formDirective.resetForm();
        this.snackBar.open('Your email address has been updated.', 'OK', { duration: SnackBarAutoDismissDurationMS });
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'Unable to update your email address.', this.errorService.getErrorMessages(error));
      });
    }
  }

  protected buildForm() {
    this.emailForm = this.formBuilder.group({
      newEmail: new UntypedFormControl('', [Validators.email, Validators.required]),
      confirmEmail: new UntypedFormControl('', [Validators.required, this.formsService.createMatchValueValidator('newEmail')])
    });
  }
}
